import { calculateDistance } from "./calculateDistance";
import { getBaseStatistics } from "./trackStatistics/getBaseStatistics";
import { getFlattenObject } from "../flattenObject";
import { getTempoStatistics } from "./trackStatistics/getTempoStatistics";
import { getElevationStatistics } from "./trackStatistics/getElevationStatistics";

const interpolatePoint = (p1, p2, fraction) => {
  // Create a new point by copying all properties from the previous point.
  const interpolated = { ...p1 };

  // Overwrite the core properties with new, interpolated values.
  interpolated.lat = p1.lat + fraction * (p2.lat - p1.lat);
  interpolated.lng = p1.lng + fraction * (p2.lng - p1.lng);

  if (p1.ele !== undefined && p2.ele !== undefined) {
    interpolated.ele = p1.ele + fraction * (p2.ele - p1.ele);
  }

  if (p1.slope !== undefined && p2.slope !== undefined) {
    interpolated.slope = p1.slope + fraction * (p2.slope - p1.slope);
  }

  if (p1.time && p2.time) {
    const time1 = new Date(p1.time).getTime();
    const time2 = new Date(p2.time).getTime();
    interpolated.time = new Date(
      time1 + fraction * (time2 - time1),
    ).toISOString();
  }

  return interpolated;
};

/**
 * Splits a track by a given distance and calculates statistics for each segment.
 *
 * @param {Array<Object>} points - The array of track points.
 * @param {number} splitDistance - The distance in meters for each split.
 * @returns {Array<Object>} An array of flattened statistics objects for each segment.
 */
export const getStatisticsByDistanceSplits = (points, splitDistance) => {
  if (!points || points.length < 2 || !splitDistance || splitDistance <= 0) {
    return [];
  }

  // 1. Create a new array and build it sequentially
  const allPoints = [];
  if (points.length > 0) {
    allPoints.push(points[0]);
  }

  let totalDistance = 0;
  let nextSplitDistance = splitDistance;

  for (let i = 1; i < points.length; i++) {
    const p1 = points[i - 1];
    const p2 = points[i];
    const segmentDistance = calculateDistance(p1.lat, p1.lng, p2.lat, p2.lng);
    const distanceBeforeSegment = totalDistance;
    totalDistance += segmentDistance;

    if (segmentDistance > 0) {
      while (totalDistance >= nextSplitDistance) {
        const distanceIntoSegment = nextSplitDistance - distanceBeforeSegment;
        const fraction = distanceIntoSegment / segmentDistance;
        const splitPoint = interpolatePoint(p1, p2, fraction);
        splitPoint.isSplitPoint = true;
        allPoints.push(splitPoint);
        nextSplitDistance += splitDistance;
      }
    }

    allPoints.push(p2);
  }

  // 2. Sorting is NO LONGER NECESSARY as we preserved the order during construction.
  // This allows the function to respect manually ordered tracks (e.g., reversed).

  // 3. Create segments based on split points.
  const segments = [];
  let currentSegment = [];

  if (allPoints.length > 0) {
    currentSegment.push(allPoints[0]);
  }

  for (let i = 1; i < allPoints.length; i++) {
    currentSegment.push(allPoints[i]);
    if (allPoints[i].isSplitPoint) {
      segments.push(currentSegment);
      currentSegment = [allPoints[i]];
    }
  }

  // Add the last remaining segment if it contains more than just the last split point
  if (currentSegment.length > 1) {
    segments.push(currentSegment);
  }

  // 4. Calculate statistics for each segment and flatten the result.
  let cumulativeDistanceForSplits = 0; // Initialize cumulative distance for splits
  const statSegements = segments.map((segment) => {
    const baseStats = getBaseStatistics(segment);
    const tempoStats = getTempoStatistics(segment);
    baseStats.statistics.speed = tempoStats.speed;
    baseStats.statistics.pace = tempoStats.pace;

    // *** elevation statistics
    const eleStats = getElevationStatistics(
      segment,
      parseInt(baseStats.statistics.distance.total / 1000),
    );
    baseStats.statistics.elevation = eleStats;

    // Add cumulative distance to the distance object
    cumulativeDistanceForSplits += baseStats.statistics.distance.total;
    baseStats.statistics.distance.cumulative = cumulativeDistanceForSplits;

    const flattened = getFlattenObject(baseStats);

    // Calculate representative slope for this segment using the enriched point data
    // Taking the average of all slopes in this segment gives a better representation
    // of the terrain character than a simple rise/run between start and end.
    const segmentSlopes = segment
      .map((p) => p.slope)
      .filter((s) => typeof s === "number");

    if (segmentSlopes.length > 0) {
      const sumSlope = segmentSlopes.reduce((acc, curr) => acc + curr, 0);
      const avgSlope = sumSlope / segmentSlopes.length;
      flattened.slope = parseFloat(avgSlope.toFixed(1));
    } else {
      flattened.slope = 0;
    }

    return flattened;
  });
  return statSegements;
};
